/**
 * Desktop-only settings.
 *
 * Renders nothing in the browser, so `App` can mount it unconditionally.
 *
 * The device block is not a convenience feature. CME counts devices, and a
 * subscriber permitted three or more concurrent logins must be reported as
 * professional — roughly $9/month becomes $140 per exchange, and the user pays
 * it. Showing them which device this is, and letting them name it, is what
 * makes the revoke list in their account usable.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { desktopBoot } from '../lib/runtime.js';
import { getPrefs, renameDevice, setAlwaysOnTop, type DesktopPrefs } from '../lib/desktop.js';
import { checkForUpdate, describe, installUpdate, type UpdateState } from '../lib/updates.js';

export function DesktopSettings(): JSX.Element | null {
  const boot = desktopBoot();
  const [prefs, setPrefs] = useState<DesktopPrefs | null>(null);
  const [label, setLabel] = useState(boot?.label ?? '');
  const [saved, setSaved] = useState<string | null>(null);
  const [update, setUpdate] = useState<UpdateState>({ kind: 'idle' });

  useEffect(() => {
    let cancelled = false;
    void getPrefs().then((p) => { if (!cancelled && p) setPrefs(p); });
    return () => { cancelled = true; };
  }, []);

  if (!boot) return null;

  const toggleOnTop = async (): Promise<void> => {
    const next = !(prefs?.alwaysOnTop ?? false);
    const applied = await setAlwaysOnTop(next);
    if (applied !== null && prefs) setPrefs({ ...prefs, alwaysOnTop: applied });
  };

  // Ignores `checkUpdatesOnLaunch` on purpose: pressing the button IS the
  // request, and a button that silently does nothing because a preference is
  // off is worse than no button.
  const check = async (): Promise<void> => {
    setUpdate({ kind: 'checking' });
    setUpdate(await checkForUpdate());
  };

  const install = async (): Promise<void> => {
    setUpdate({ kind: 'installing' });
    setUpdate(await installUpdate());
  };

  const commitLabel = async (): Promise<void> => {
    const trimmed = label.trim();
    if (!trimmed || trimmed === boot.label) return;
    const updated = await renameDevice(trimmed);
    setSaved(updated ? 'Saved.' : 'Could not save that name.');
    window.setTimeout(() => setSaved(null), 2500);
  };

  return (
    <>
      <section className="settings-block">
        <h3 className="panel-h">DESKTOP</h3>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={prefs?.alwaysOnTop ?? false}
            onChange={() => void toggleOnTop()}
          />
          <span>
            Keep AEGIS above other windows
            <em>
              For running beside your trading platform. Off by default.
            </em>
          </span>
        </label>
        <dl className="kv">
          <dt>Platform</dt><dd className="mono">{boot.platform}</dd>
          <dt>Version</dt><dd className="mono">{boot.appVersion}</dd>
          <dt>API</dt><dd className="mono">{boot.apiBaseUrl}</dd>
        </dl>

        <div className="field-row">
          <button
            className="btn"
            onClick={() => void check()}
            disabled={update.kind === 'checking' || update.kind === 'installing'}
          >
            Check for updates
          </button>
          {update.kind === 'available' && (
            <button className="btn btn-primary" onClick={() => void install()}>
              Install and restart
            </button>
          )}
        </div>
        {describe(update) && (
          <p className="panel-body" role={update.kind === 'failed' ? 'alert' : 'status'}>
            {describe(update)}
          </p>
        )}
      </section>

      <section className="settings-block">
        <h3 className="panel-h">THIS DEVICE</h3>
        <p className="panel-body">
          Exchanges licence market data per device, so every device that can display
          it is registered and can be revoked. Naming this one makes it recognisable
          in your device list.
        </p>
        <div className="field-row">
          <input
            className="input"
            value={label}
            maxLength={64}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => void commitLabel()}
            aria-label="Device name"
          />
          <button className="btn" onClick={() => void commitLabel()}>Save</button>
        </div>
        {saved && <p className="panel-body">{saved}</p>}
        <dl className="kv">
          <dt>Device ID</dt><dd className="mono">{boot.deviceId}</dd>
        </dl>
      </section>
    </>
  );
}
